// Arranque del PAE Rápido: qué ve el estudiante en la primera pantalla.
//
// Hallazgo (informe "Cómo estudian", 27-ago-2026, maqueta en
// docs/pitch/preview-pae-rapido-arranque/): 121 de 166 estudiantes (73%) no
// pasaban de la primera pantalla, que pedía cuatro datos administrativos y un
// botón apagado ANTES de cualquier pregunta sobre el paciente. El fundador
// eligió el 2026-09-02 la variante B, "primero el paciente": una caja con
// dictado para contar lo que llamó la atención, la pregunta de cómo ordenarlo
// (Henderson recomendado o formato libre) y los datos al final.
//
// Segunda iteración (feedback del fundador en producción, mismo día: "poco
// intuitivo, feo y poco autoexplicativo; quizá dar la opción de hacerlo como
// antes"): el arranque se elige POR DISPOSITIVO. Un enlace sobrio en cada
// arranque lleva al otro y la preferencia se recuerda en localStorage. La
// resolución final es: flag apagado ⇒ 'hoy' (el kill-switch manda sobre
// cualquier preferencia); si no, la preferencia guardada si es válida; si no,
// 'b'. Todo fail-safe hacia la variante por flag: storage que revienta o un
// valor fuera del catálogo se ignoran.
//
// Este módulo es puro (sin React) para que el flag, el catálogo y la
// preferencia se prueben solos y para que App.jsx etiquete la telemetría con
// la MISMA variante (y el mismo origen) que renderiza RapidoStep1.
//
// Kill-switch ON por defecto (norma del repo: !== 'false', nunca === 'true').

import { esModeloValoracion, marcasRapido } from './paeModelos';

// Catálogo cerrado: viaja a eventos_v2 en `pae_rapido_arranque.variante` y en
// `pae_rapido_arranque_cambio.a`. 'a' y 'c' existieron solo como maqueta; se
// agregan aquí si algún día se implementan, nunca como texto libre.
export const VARIANTES_ARRANQUE = ['hoy', 'b'];

// De dónde salió la variante efectiva (catálogo cerrado para telemetría).
export const ORIGENES_ARRANQUE = ['flag', 'preferencia'];

export const CLAVE_PREFERENCIA_ARRANQUE = 'mipae:pae:arranque';

export function paeArranqueV2Habilitado(env = process.env) {
  return env.REACT_APP_PAE_ARRANQUE_V2 !== 'false';
}

// localStorage se resuelve en cada llamada (no al cargar el módulo): en tests
// y en entornos sin ventana simplemente no hay preferencia.
function storagePorDefecto() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

// Preferencia guardada en este dispositivo, o null si no hay, no es válida o
// el storage revienta (modo privado, cuota, storage bloqueado).
export function preferenciaArranque(storage = storagePorDefecto()) {
  try {
    const v = storage?.getItem(CLAVE_PREFERENCIA_ARRANQUE);
    return VARIANTES_ARRANQUE.includes(v) ? v : null;
  } catch {
    return null;
  }
}

// Recuerda la variante para este dispositivo. Devuelve si quedó guardada; un
// valor fuera del catálogo no se escribe nunca (el storage es el que viaja
// después a telemetría, y solo acepta catálogo).
export function preferirArranque(variante, storage = storagePorDefecto()) {
  if (!VARIANTES_ARRANQUE.includes(variante)) return false;
  try {
    if (!storage) return false;
    storage.setItem(CLAVE_PREFERENCIA_ARRANQUE, variante);
    return true;
  } catch {
    return false;
  }
}

// Variante efectiva + origen. Se lee en cada render (no en la carga del
// módulo) para que el kill-switch y la preferencia sean verificables por test
// sin resetear módulos.
export function resolverArranque(env = process.env, storage = storagePorDefecto()) {
  if (!paeArranqueV2Habilitado(env)) return { variante: 'hoy', origen: 'flag' };
  const pref = preferenciaArranque(storage);
  if (pref) return { variante: pref, origen: 'preferencia' };
  return { variante: 'b', origen: 'flag' };
}

export function varianteArranque(env = process.env, storage = storagePorDefecto()) {
  return resolverArranque(env, storage).variante;
}

// Orden con el que se retoma el paso 1 si el usuario vuelve desde el paso 3
// (o desde la confirmación libre, o cambia de arranque a mitad del PAE): lo
// que ya eligió se respeta, sin volver a preguntar. El orden es 'libre' o el
// id del modelo de enfermería con que valora (paeModelos.js). Sin modelo
// libre ni nada marcado en su modelo, todavía no eligió.
//
// Un modelo que llega ELEGIDO de antes también cuenta (`modeloElegido`, ver
// `rapidoEnModeloElegido`): la ficha de la Enciclopedia o el modelo recordado
// del dispositivo. `modelo` a secas NO cuenta, porque el paso 1 lo escribe
// siempre al montar (Henderson si no hay nada recordado) y eso no es una
// elección de la persona (revisión de código, 3-oct-2026).
export function ordenInicialArranque(rapido = {}) {
  if (rapido.modelo === 'libre') return 'libre';
  if (rapido.modeloElegido === true && esModeloValoracion(rapido.modelo)) return rapido.modelo;
  const modelo = esModeloValoracion(rapido.modelo) ? rapido.modelo : 'henderson';
  const marcas = marcasRapido({ ...rapido, modelo });
  const hayMarcada = Object.values(marcas).some((n) => n && n.alterada);
  return hayMarcada ? modelo : null;
}

// El borrador con un modelo que ya viene elegido, para que el paso 1 abra
// con ese modelo marcado y su lista a la vista (el plan prometía "abre el PAE
// ya en ese modelo" y el radio quedaba sin marcar). Lo usan "Armar un PAE con
// este modelo" (App.jsx, onAbrirPae), que pone el modelo aunque haya un
// borrador anterior sin borrar lo que ese borrador tenía, y el montaje del
// paso 1 cuando hay un modelo recordado. Un id fuera del catálogo no toca
// nada. La marca es estado de la pantalla: no se guarda en remoto
// (`datosRapidoParaGuardar`) ni viaja a la IA.
export function rapidoEnModeloElegido(rapido, modeloId) {
  if (!esModeloValoracion(modeloId)) return rapido;
  return { ...(rapido || {}), modelo: modeloId, modeloElegido: true };
}

// Los cuatro datos se piden recién cuando ya hay trabajo clínico hecho: algo
// marcado en el modelo elegido o alguna de las preguntas libres con texto.
// El relato inicial NO cuenta: escribirlo es opcional y no ordena nada.
export function datosListosParaPedir(orden, rapido = {}) {
  if (esModeloValoracion(orden)) {
    const marcas = marcasRapido({ ...rapido, modelo: orden });
    return Object.values(marcas).some((n) => n && n.alterada);
  }
  if (orden === 'libre') {
    const libre = rapido.libre || {};
    return ['confirman', 'se_debe', 'que_mas'].some((k) => (libre[k] || '').trim());
  }
  return false;
}

// Lo que se guarda en remoto al terminar un PAE Rápido. En modo Henderson el
// relato de la caja de arranque (`rapido.libre`) no alimenta ningún paso, ni
// el PDF ni Mis PAE: es texto libre sobre un paciente que ya cumplió su
// función (elegir necesidades) y no tiene por qué persistir. En modo libre
// SÍ es parte del PAE y se guarda como siempre. Auditoría 2026-09-02.
//
// Misma regla con los modelos de valoración (2026-10-02): se guarda solo lo
// marcado en el modelo con que terminó. Si empezó en Gordon y cambió a
// Henderson, los hallazgos que escribió en Gordon no se usaron y no viajan.
// Y en formato libre no viaja ningún hallazgo de modelo: no alimentan nada y
// podían llevar un dato que la guarda había bloqueado (revisión clínica).
export function datosRapidoParaGuardar(formData) {
  const rapido = formData?.rapido;
  if (!rapido) return formData;
  let resto = rapido;
  if ('modeloElegido' in resto) {
    const { modeloElegido, ...sinMarca } = resto;
    resto = sinMarca;
  }
  if (rapido.modelo !== 'libre' && rapido.libre) {
    const { libre, ...sinLibre } = resto;
    resto = sinLibre;
  }
  if (rapido.modelo === 'libre' && (resto.necesidades || resto.categorias)) {
    const { necesidades, categorias, ...sinMarcas } = resto;
    resto = sinMarcas;
  }
  if (resto.categorias) {
    const activo = esModeloValoracion(resto.modelo) && resto.modelo !== 'henderson' ? resto.modelo : null;
    const { categorias, ...sinCategorias } = resto;
    resto = activo && categorias[activo] ? { ...sinCategorias, categorias: { [activo]: categorias[activo] } } : sinCategorias;
    if (activo && resto.necesidades) {
      const { necesidades, ...sinNecesidades } = resto;
      resto = sinNecesidades;
    }
  }
  return resto === rapido ? formData : { ...formData, rapido: resto };
}

// Con qué datos arranca un PAE Rápido NUEVO (startMode en App.jsx). Hay tres
// puertas y antes cada una dejaba otra cosa (auditoría estudiante, 4-oct-2026):
//  - Si el PAE anterior ya se guardó o se reabrió desde Tus PAEs
//    (`partirDeCero`), el nuevo parte vacío: arrastrar el paciente, la
//    valoración y el plan de un caso cerrado a uno nuevo mezclaba dos
//    pacientes. Solo sobrevive el modelo elegido en la puerta (la ficha de un
//    modelo o Benito); el modelo recordado del dispositivo lo vuelve a poner el
//    paso 1 al montar.
//  - Si es un borrador en curso, se conserva como siempre (salir por la barra
//    de pestañas y volver no puede borrar lo escrito), salvo el plan NOC/NIC
//    viejo, que haría arrancar el paso 3 en la fase de plan con datos ajenos.
//  - El modelo de la puerta se aplica con `rapidoEnModeloElegido`, la misma
//    regla de "Armar un PAE con este modelo"; un id fuera del catálogo no toca
//    nada.
// Devuelve el MISMO objeto si nada cambió.
export function formDataAlIniciarRapido(formData, { partirDeCero = false, modelo = null } = {}) {
  const base = partirDeCero ? {} : (formData || {});
  let rapido = base.rapido;
  if (rapido?._plan) rapido = { ...rapido, _plan: undefined };
  if (esModeloValoracion(modelo)) rapido = rapidoEnModeloElegido(rapido, modelo);
  if (rapido === base.rapido) return base;
  return { ...base, rapido };
}
